import { Component, computed, model } from '@angular/core';

export type TIconStyle = 'outlined' | 'round' | 'sharp' | 'two-tone';

@Component({
    selector: 'app-icon',
    templateUrl: './icon.component.html',
    styleUrls: ['./icon.component.scss'],
})
export class IconComponent {
    /***
     * The key of the icon to display. This can be either an icon or a symbol.
     * @param value The key of the icon to display.
     * @example
     * 'icon-add' for an "add" icon
     * 'symbol-add' for an "add" symbol
     * 'add' for an "add" icon, as omitting the prefix defaults to an icon.
     */
    public readonly key = model<string>(null);

    /***
     * The icon to display.
     * @param value The key of the icon to display.
     * @example
     * 'add' for an "add" icon.
     */
    public readonly icon = model<string>(null);

    /***
     * The symbol to display.
     * @param value The key of the symbol to display.
     * @example
     * 'add' for an "add" symbol.
     */
    public readonly symbol = model<string>(null);

    /***
     * The style of the icon or symbol to display.
     * @param value The style name.
     */
    public readonly style = model<TIconStyle>(null);

    /***
     * Whether to mirror the icon or symbol horizontally.
     * @param value True to mirror, false otherwise.
     */
    public readonly mirrored = model(false);

    protected readonly value = computed(() => {
        const key = this.key();
        const icon = this.icon();
        const symbol = this.symbol();

        return (key ?? icon ?? symbol)?.replace(/^(icon-|symbol-)/, '');
    });

    protected readonly iconClass = computed(() => {
        const key = this.key();
        const icon = this.icon();
        const mirrored = this.mirrored();

        const isIcon = (key && !key.startsWith('symbol-')) || !!icon;
        // Only the outlined Material Symbols font is loaded, so symbols default to the outlined style.
        const style = this.style() ?? (isIcon ? null : 'outlined');

        return (
            ['material', isIcon ? 'icons' : 'symbols', style].filter(Boolean).join('-') + (mirrored ? ' mirrored' : '')
        );
    });
}
